import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Tag } from '@carbon/react';
import FormattedDate from 'src/components/Base/FormattedDate';
import { IssueStatusTag, IssueTypeTag } from 'src/components/Issue/IssueTags';
import { useMoney } from 'src/hooks/useMoney';
import { IssueEntity } from 'src/services/apis/types';
import { employeeName } from 'src/utils/employee';

interface IssueDetailsProps {
  issue: IssueEntity | null;
  onClose: () => void;
}

/** A read-only look at one issue, opened from a board card. Editing stays in the form modal. */
const IssueDetails = React.memo(({ issue, onClose }: IssueDetailsProps) => {
  const { t } = useTranslation();
  const { format: formatCurrency } = useMoney();

  return (
    <Modal open={!!issue} passiveModal size="sm" modalHeading={issue?.title} onRequestClose={onClose}>
      {issue && (
        <dl className="issue-details">
          <dt>{t('common.type')}</dt>
          <dd>
            <IssueTypeTag type={issue.type} />
          </dd>
          <dt>{t('common.status')}</dt>
          <dd>
            <IssueStatusTag status={issue.status} />
          </dd>
          <dt>{t('common.employee')}</dt>
          <dd>{employeeName(issue.employee) || t('common.noEmployee')}</dd>
          <dt>{t('common.fine')}</dt>
          <dd>{formatCurrency(issue.fine)}</dd>
          <dt>{t('common.openedAt')}</dt>
          <dd>
            <FormattedDate date={issue.createdAt} format={'DD/MM/YYYY'} />
          </dd>
          {issue.description && (
            <>
              <dt>{t('common.description')}</dt>
              <dd className="issue-details__text">{issue.description}</dd>
            </>
          )}
          {issue.notes && (
            <>
              <dt>{t('common.notes')}</dt>
              <dd className="issue-details__text">{issue.notes}</dd>
            </>
          )}
          {issue.tags?.length > 0 && (
            <>
              <dt>{t('common.tags')}</dt>
              <dd>
                {issue.tags.map((tag) => (
                  <Tag key={tag} size="sm" type="outline">
                    {tag}
                  </Tag>
                ))}
              </dd>
            </>
          )}
        </dl>
      )}
    </Modal>
  );
});

IssueDetails.displayName = 'IssueDetails';

export default IssueDetails;
